import { createApp } from 'vue'
import i18n from './i18n'
import { createVuestic } from 'vuestic-ui'
import { createGtm } from '@gtm-support/vue-gtm'

import piniaStores from './stores'
import vuesticGlobalConfig from './services/vuestic-ui/global-config'
import App from './App.vue'

import { msalInstance } from './authConfig'
import { PublicClientApplication } from '@azure/msal-browser'
import router from './router'
import { useAppConfigStore } from './stores/app-config-store'
import axiosUtil from './services/axios-helper'
import type { AppConfig } from './stores/app-config-store'
import { ToastService } from './services/toast-service'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment

const initializeAppConfigs = async (stores: any, msalInstance: PublicClientApplication) => {
  try {
    const response = await (
      await axiosUtil(msalInstance, {
        isAcquireAccessToken: false,
      })
    ).get('/api/app-configs-public')

    const appConfigs: AppConfig = response.data

    // log the fetched configuration data
    console.log('Fetched app configs:', appConfigs)

    const configStore = useAppConfigStore(stores)

    // Store the fetched configuration data in Pinia store
    configStore.loadAppConfigs(appConfigs)

    return true
  } catch (error) {
    console.error('Failed to load app configs:', error)
    throw error
  }
}

const initializeApp = async () => {
  try {
    // log the initialization start
    console.log('Initializing the app...')

    await msalInstance.initialize().catch((error) => {
      // log the error if initialization fails
      console.error('MSAL Initialization Error:', error)
    })

    await msalInstance
      .handleRedirectPromise()
      .then((result) => {
        /*
        example of the result object:
        {
          "authority": "https://pwinno.b2clogin.com/pwinno.onmicrosoft.com/b2c_1_pwinno_signup_or_signin/",
          "uniqueId": "27f0b5ad-657d-4eca-bc95-df5f025a0737",
          "tenantId": "",
          "scopes": [],
          "account": {
              "homeAccountId": "27f0b5ad-657d-4eca-bc95-df5f025a0737-b2c_1_pwinno_signup_or_signin.4e4e3b18-2dc2-4574-8833-6149bb91e282",
              "environment": "pwinno.b2clogin.com",
              "tenantId": "B2C_1_PWINNO_SIGNUP_OR_SIGNIN",
              "username": "isaacchan@pwinno.com",
              "localAccountId": "27f0b5ad-657d-4eca-bc95-df5f025a0737",
              "name": "Isaac Chan",
              "authorityType": "MSSTS",
              "tenantProfiles": {},
              "idTokenClaims": {
                  "exp": 1750650296,
                  "nbf": 1750646696,
                  "ver": "1.0",
                  "iss": "https://pwinno.b2clogin.com/4e4e3b18-2dc2-4574-8833-6149bb91e282/v2.0/",
                  "sub": "27f0b5ad-657d-4eca-bc95-df5f025a0737",
                  "aud": "f1bbe77b-95fa-4348-839c-63cf012a8ce3",
                  "nonce": "01979aab-5e53-732f-a8fe-edb074cb092d",
                  "iat": 1750646696,
                  "auth_time": 1750646695,
                  "isForgotPassword": true,
                  "name": "Isaac Chan",
                  "emails": [
                      "isaacchan@pwinno.com"
                  ],
                  "tfp": "B2C_1_PWINNO_SIGNUP_OR_SIGNIN"
              },
              "idToken": "eyJhbGciOiJSUzI1NiIsImtpZCI6Ilg1ZVhrNHh5b2pORnVtMWtsMll0djhkbE5QNC1jNTdkTzZRR1RWQndhTmsiLCJ0eXAiOiJKV1QifQ.eyJleHAiOjE3NTA2NTAyOTYsIm5iZiI6MTc1MDY0NjY5NiwidmVyIjoiMS4wIiwiaXNzIjoiaHR0cHM6Ly9wd2lubm8uYjJjbG9naW4uY29tLzRlNGUzYjE4LTJkYzItNDU3NC04ODMzLTYxNDliYjkxZTI4Mi92Mi4wLyIsInN1YiI6IjI3ZjBiNWFkLTY1N2QtNGVjYS1iYzk1LWRmNWYwMjVhMDczNyIsImF1ZCI6ImYxYmJlNzdiLTk1ZmEtNDM0OC04MzljLTYzY2YwMTJhOGNlMyIsIm5vbmNlIjoiMDE5NzlhYWItNWU1My03MzJmLWE4ZmUtZWRiMDc0Y2IwOTJkIiwiaWF0IjoxNzUwNjQ2Njk2LCJhdXRoX3RpbWUiOjE3NTA2NDY2OTUsImlzRm9yZ290UGFzc3dvcmQiOnRydWUsIm5hbWUiOiJJc2FhYyBDaGFuIiwiZW1haWxzIjpbImlzYWFjY2hhbkBwd2lubm8uY29tIl0sInRmcCI6IkIyQ18xX1BXSU5OT19TSUdOVVBfT1JfU0lHTklOIn0.TMbHdFmybxjmuXuvll8U5hqecPOpzHwWSHLEYbplkM1gy7IfVv4fvnFjc4vHAhIjbBa6Cyb6hniqLQdDOXTeV_Tu6obiTYTP0nuH6BrlzpFAhhMXeyhzfG7QwXHR8zwyH74eGpRcEoE9U_AMYyUaWJCnzEiYMx8HzD-GRtNSKqN0S6kBIaQj-wljxJSmUFKdyLn4tEEXp4DkqAFSU17B40AU5g2cIgRHnRYjMPhxi0-z3X6rQahktidsLdYUf1FjhI0x-lDxn83Ff57JAmGGyvbzNI0Luds3xrpa8JsUtXjmuJfof7ZZHz-eyO4d7NP124qWbn9aTsTJOezSm1J92w"
          },
          "idToken": "eyJhbGciOiJSUzI1NiIsImtpZCI6Ilg1ZVhrNHh5b2pORnVtMWtsMll0djhkbE5QNC1jNTdkTzZRR1RWQndhTmsiLCJ0eXAiOiJKV1QifQ.eyJleHAiOjE3NTA2NTAyOTYsIm5iZiI6MTc1MDY0NjY5NiwidmVyIjoiMS4wIiwiaXNzIjoiaHR0cHM6Ly9wd2lubm8uYjJjbG9naW4uY29tLzRlNGUzYjE4LTJkYzItNDU3NC04ODMzLTYxNDliYjkxZTI4Mi92Mi4wLyIsInN1YiI6IjI3ZjBiNWFkLTY1N2QtNGVjYS1iYzk1LWRmNWYwMjVhMDczNyIsImF1ZCI6ImYxYmJlNzdiLTk1ZmEtNDM0OC04MzljLTYzY2YwMTJhOGNlMyIsIm5vbmNlIjoiMDE5NzlhYWItNWU1My03MzJmLWE4ZmUtZWRiMDc0Y2IwOTJkIiwiaWF0IjoxNzUwNjQ2Njk2LCJhdXRoX3RpbWUiOjE3NTA2NDY2OTUsImlzRm9yZ290UGFzc3dvcmQiOnRydWUsIm5hbWUiOiJJc2FhYyBDaGFuIiwiZW1haWxzIjpbImlzYWFjY2hhbkBwd2lubm8uY29tIl0sInRmcCI6IkIyQ18xX1BXSU5OT19TSUdOVVBfT1JfU0lHTklOIn0.TMbHdFmybxjmuXuvll8U5hqecPOpzHwWSHLEYbplkM1gy7IfVv4fvnFjc4vHAhIjbBa6Cyb6hniqLQdDOXTeV_Tu6obiTYTP0nuH6BrlzpFAhhMXeyhzfG7QwXHR8zwyH74eGpRcEoE9U_AMYyUaWJCnzEiYMx8HzD-GRtNSKqN0S6kBIaQj-wljxJSmUFKdyLn4tEEXp4DkqAFSU17B40AU5g2cIgRHnRYjMPhxi0-z3X6rQahktidsLdYUf1FjhI0x-lDxn83Ff57JAmGGyvbzNI0Luds3xrpa8JsUtXjmuJfof7ZZHz-eyO4d7NP124qWbn9aTsTJOezSm1J92w",
          "idTokenClaims": {
              "exp": 1750650296,
              "nbf": 1750646696,
              "ver": "1.0",
              "iss": "https://pwinno.b2clogin.com/4e4e3b18-2dc2-4574-8833-6149bb91e282/v2.0/",
              "sub": "27f0b5ad-657d-4eca-bc95-df5f025a0737",
              "aud": "f1bbe77b-95fa-4348-839c-63cf012a8ce3",
              "nonce": "01979aab-5e53-732f-a8fe-edb074cb092d",
              "iat": 1750646696,
              "auth_time": 1750646695,
              "isForgotPassword": true,
              "name": "Isaac Chan",
              "emails": [
                  "isaacchan@pwinno.com"
              ],
              "tfp": "B2C_1_PWINNO_SIGNUP_OR_SIGNIN"
          },
          "accessToken": "",
          "fromCache": false,
          "expiresOn": null,
          "correlationId": "01979aab-5e52-7198-b93e-d15c3be66151",
          "requestId": "",
          "familyId": "",
          "tokenType": "",
          "state": "",
          "cloudGraphHostName": "",
          "msGraphHost": "",
          "fromNativeBroker": false
      }


        */

        // log the result of the redirect handling
        console.log('Redirect handling result:', result)

        // check if idTokenClaims.isForgotPassword is true, if so, log the user out
        if (
          result &&
          result.idTokenClaims &&
          (result.idTokenClaims as { isForgotPassword?: boolean }).isForgotPassword
        ) {
          console.warn('User is in forgot password flow, logging out...')
          msalInstance.logoutRedirect({
            postLogoutRedirectUri: import.meta.env.VITE_APP_BASE_URL,
          })
          return
        }

        if (result) {
          // If the result is not null, set the active account
          msalInstance.setActiveAccount(result.account)

          // Clear any auth-related toast messages since login was successful
          // This prevents stale "session refresh" toasts from showing after successful redirect
          ToastService.clearByCategory('auth')
        } else {
          // If no result, check for an active account
          const activeAccount = msalInstance.getActiveAccount()
          if (activeAccount) {
            msalInstance.setActiveAccount(activeAccount)
          }
        }
      })
      .catch((error: any) => {
        // Log the error if redirect handling fails
        console.error('Error handling redirect:', error)

        // Store the error for Login.vue to display
        try {
          const errorMessage =
            error?.errorMessage ||
            error?.message ||
            (error?.errorCode ? `Authentication error: ${error.errorCode}` : 'Authentication failed')

          sessionStorage.setItem(
            'authError',
            JSON.stringify({
              message: errorMessage,
              errorCode: error?.errorCode,
              timestamp: Date.now(),
            }),
          )
        } catch {
          // Ignore storage errors
        }
      })

    const app = createApp(App)

    app.use(piniaStores)

    // The next 2 lines are optional. This is how you configure MSAL to take advantage of the router's navigate functions when MSAL redirects between pages in your app
    // const navigationClient = new CustomNavigationClient(router)
    // msalInstance.setNavigationClient(navigationClient)
    // msalInstance.addEventCallback((event) => {
    //   // log the event type and payload
    //   console.log('MSAL event:', event.eventType, event.payload)

    //   if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    //     const payload = event.payload as AuthenticationResult
    //     const account = payload.account
    //     msalInstance.setActiveAccount(account)
    //   } else if (event.eventType === EventType.LOGOUT_SUCCESS) {
    //     msalInstance.setActiveAccount(null)
    //   } else if (event.eventType === EventType.LOGIN_FAILURE) {
    //     alert('Login failure due to ' + event.error)

    //     console.error('Login failure:', event.error)
    //   } else if (event.eventType === EventType.HANDLE_REDIRECT_START) {
    //     // This event is triggered when the app is handling a redirect response
    //     console.log('Handling redirect start...')
    //   } else if (event.eventType === EventType.HANDLE_REDIRECT_END) {
    //     // This event is triggered when the app has finished handling a redirect response
    //     console.log('Redirect handling completed.')
    //   }
    // })

    // app.use(msalPlugin, msalInstance)
    app.use(i18n)
    app.use(createVuestic({ config: vuesticGlobalConfig }))

    if (import.meta.env.VITE_APP_GTM_ENABLED) {
      app.use(
        createGtm({
          id: import.meta.env.VITE_APP_GTM_KEY,
          debug: false,
          // @gtm-support/vue-gtm currently ships its own vue-router types.
          // Cast to avoid a type mismatch; runtime object is the same router.
          vueRouter: router as any,
        }),
      )
    }

    app.use(router)

    try {
      await initializeAppConfigs(piniaStores, msalInstance)
    } catch (error: any) {
      console.error('Failed to initialize app configs:', error)
      let errorType = 'unknown'
      let errorMessage = ''
      if (error.response) {
        errorType = 'server'
        errorMessage = `Server error: ${error.response.status} ${error.response.statusText}`
      } else if (error.request) {
        errorType = 'network'
        errorMessage = 'Network error: Unable to reach the server.'
      } else {
        errorType = 'other'
        errorMessage = error.message || 'Unknown error'
      }
      router.push({
        name: 'error',
        query: { type: errorType, message: errorMessage },
      })
    }

    app.mount('#app')
  } catch (error) {
    console.error('Failed to initialize the app:', error)

    // alert the user that the app failed to initialize
    alert('Failed to initialize the app. Please try again later.')
  }
}

initializeApp()
